import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import SEOHead from '../components/shared/SEOHead';
import { revokeConsent, setConsent, CONSENT_STATES } from '../utils/consent';

const CookiePolicyPage = () => {
  const handleRevoke = () => {
    revokeConsent();
    window.location.reload();
  };

  const handleAccept = () => {
    setConsent(CONSENT_STATES.ACCEPTED);
    window.location.reload();
  };

  return (
    <PageLayout>
      <SEOHead title="Cookie Policy | Kalobrate" description="How Kalobrate uses cookies. Manage your cookie preferences." canonical="/cookie-policy" />

      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-carbon mb-6 tracking-tight">Cookie Policy</h1>
          <p className="text-carbon/50 text-sm mb-8">Last updated: July 2026</p>

          <div className="space-y-8 text-sm text-carbon/80 leading-relaxed">
            <div>
              <h2 className="text-xl font-heading font-bold text-carbon mb-3">What are cookies?</h2>
              <p>Cookies are small text files stored on your device when you visit a website. They help the website function, remember your preferences and understand how visitors use the site.</p>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-carbon mb-3">Your consent</h2>
              <p className="mb-3">When you first visit this website, you are shown a cookie consent banner. Analytics cookies are <strong className="text-carbon">not loaded</strong> until you explicitly accept them. If you reject cookies or take no action, only essential cookies are used.</p>
              <p>If your browser sends a Do Not Track (DNT) signal, analytics cookies are not loaded by default.</p>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-carbon mb-3">Essential cookies</h2>
              <p className="mb-3">These are required for the website to function correctly. They do not track you or collect personal information.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead><tr className="bg-gray-50"><th className="text-left px-4 py-2 border-b font-semibold text-carbon">Cookie</th><th className="text-left px-4 py-2 border-b font-semibold text-carbon">Purpose</th><th className="text-left px-4 py-2 border-b font-semibold text-carbon">Duration</th></tr></thead>
                  <tbody>
                    <tr><td className="px-4 py-2 border-b">kalobrate_cookie_consent</td><td className="px-4 py-2 border-b">Stores your cookie consent preference</td><td className="px-4 py-2 border-b">Persistent (localStorage)</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-carbon mb-3">Analytics cookies</h2>
              <p className="mb-3">These cookies help us understand how visitors use the website. The data collected is anonymous and aggregated. It is not used for advertising, remarketing or profiling. These cookies are <strong className="text-carbon">only loaded after you accept</strong> the cookie consent banner.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead><tr className="bg-gray-50"><th className="text-left px-4 py-2 border-b font-semibold text-carbon">Cookie</th><th className="text-left px-4 py-2 border-b font-semibold text-carbon">Provider</th><th className="text-left px-4 py-2 border-b font-semibold text-carbon">Purpose</th><th className="text-left px-4 py-2 border-b font-semibold text-carbon">Duration</th></tr></thead>
                  <tbody>
                    <tr><td className="px-4 py-2 border-b">_ga</td><td className="px-4 py-2 border-b">Google Analytics</td><td className="px-4 py-2 border-b">Distinguishes visitors</td><td className="px-4 py-2 border-b">2 years</td></tr>
                    <tr><td className="px-4 py-2 border-b">_ga_*</td><td className="px-4 py-2 border-b">Google Analytics</td><td className="px-4 py-2 border-b">Maintains session state</td><td className="px-4 py-2 border-b">2 years</td></tr>
                    <tr><td className="px-4 py-2 border-b">_gid</td><td className="px-4 py-2 border-b">Google Analytics</td><td className="px-4 py-2 border-b">Distinguishes visitors (24h)</td><td className="px-4 py-2 border-b">24 hours</td></tr>
                    <tr><td className="px-4 py-2 border-b">ph_*</td><td className="px-4 py-2 border-b">PostHog</td><td className="px-4 py-2 border-b">Anonymous usage analytics</td><td className="px-4 py-2 border-b">1 year</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-carbon mb-3">What data is collected</h2>
              <p className="mb-3">When analytics cookies are active, the following anonymous data may be collected:</p>
              <ul className="space-y-1 ml-4 list-disc">
                <li>Pages visited and time spent on each page</li>
                <li>How you arrived at the site (referral source)</li>
                <li>Browser type and device category</li>
                <li>Approximate geographic region (not precise location)</li>
                <li>Actions taken on the site (e.g. button clicks, video views)</li>
              </ul>
              <p className="mt-3">This data is aggregated and cannot be used to identify you personally. It is not shared with third parties for advertising or marketing.</p>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-carbon mb-3">Managing your preferences</h2>
              <p className="mb-4">You can change your cookie preferences at any time using the buttons below.</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={handleAccept} className="bg-golden text-carbon px-5 py-2 text-sm font-semibold hover:bg-golden/90 transition-colors" data-testid="cookie-policy-accept">Accept analytics cookies</button>
                <button onClick={handleRevoke} className="border border-gray-200 text-carbon px-5 py-2 text-sm font-semibold hover:bg-gray-50 transition-colors" data-testid="cookie-policy-revoke">Reject analytics cookies</button>
              </div>
              <p className="mt-3">You can also control cookies through your browser settings. Most browsers allow you to block or delete cookies, though this may affect the functionality of some websites.</p>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-carbon mb-3">Do Not Track</h2>
              <p>If your browser sends a Do Not Track (DNT) signal, this website will not load analytics cookies by default. You would need to explicitly accept cookies for analytics to be activated.</p>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-carbon mb-3">Contact</h2>
              <p>If you have questions about this cookie policy, contact <a href="mailto:ollie@kalobrate.com" className="text-carbon hover:text-golden transition-colors underline">ollie@kalobrate.com</a>.</p>
              <p className="mt-2">For more information about how we handle personal data, see our <Link to="/privacy" className="text-carbon hover:text-golden transition-colors underline">Privacy Policy</Link>.</p>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default CookiePolicyPage;
